import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {AsyncHandler, Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  computeSidePanelTrackingFrameName,
  computeSidePanelTrackingTrackingFramePayload,
} from '../../../../helpers/sidePanelTrackingHelpers';
import {FrameTypesEnum} from '../../../../models/api/apiTypes';
import {IntegrationTypeModel} from '../../../../models/api/integrationTypesModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {AsyncStatus, ResourceMap, ResourceTableStatusesEnum} from '../../../../store/appTypes';
import {SidePanelFrame} from '../../../../store/stateless/sidePanelFrameSelectors';
import {ApplicationCardSkeleton} from '../../../common/applications/skeleton/applicationCardSkeleton';
import {DropdownEmpty} from '../../../common/dropdowns/dropdownEmpty';
import {DropdownLoader} from '../../../common/dropdowns/dropdownLoader';
import {SidePanelApplicationsManagerFindMoreBanner} from './banner/sidePanelApplicationsManagerFindMoreBanner';
import {SidePanelApplicationFrameCard} from './sidePanelApplicationFrameCard';
import {SidePanelApplicationsManagerInfoBox} from './sidePanelApplicationsManagerInfoBox';

/*
 * Props.
 */

interface SidePanelApplicationsManagerContentProps {
  integrationTypesAsyncStatus: AsyncStatus;
  pinnedFrames: ReadonlyArray<SidePanelFrame>;
  unpinnedFrames: ReadonlyArray<SidePanelFrame>;
  isSearching: boolean;
  isInfoBoxExpanded: boolean;
  integrationTypes: ResourceMap<IntegrationTypeModel>;
  applicationsAsyncStatus: AsyncStatus;
  hasNewApplicationsEntryPoints?: boolean;

  onToggleInfoBox: Handler;
  onTogglePinned: HandlerOf<SidePanelFrame>;
  fetchIntegrationTypesAsync: AsyncHandler;
  fetchApplicationsAsync: AsyncHandler;
}

/*
 * Style.
 */

const StyledHeaderWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  margin: 20px 0 12px 0;
`;

const StyledHeaderTitleDiv = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
`;

const StyledHeaderDescriptionDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

export const SidePanelApplicationsManagerContent: FC<SidePanelApplicationsManagerContentProps> = (props) => {
  const {
    isSearching,
    pinnedFrames,
    unpinnedFrames,
    isInfoBoxExpanded,
    integrationTypes,
    integrationTypesAsyncStatus,
    applicationsAsyncStatus,
    hasNewApplicationsEntryPoints,
    onToggleInfoBox,
    onTogglePinned,
    fetchIntegrationTypesAsync,
    fetchApplicationsAsync,
  } = props;

  // If we are searching and have no results.
  if (isSearching && pinnedFrames.length === 0 && unpinnedFrames.length === 0) {
    return <DropdownEmpty />;
  }

  if (integrationTypesAsyncStatus.status === ResourceTableStatusesEnum.FAILED) {
    return renderDropdownLoader(fetchIntegrationTypesAsync);
  }

  if (applicationsAsyncStatus.status === ResourceTableStatusesEnum.FAILED) {
    return renderDropdownLoader(fetchApplicationsAsync);
  }

  const isIntegrationTypesLoading =
    integrationTypesAsyncStatus.status === ResourceTableStatusesEnum.INITIAL ||
    integrationTypesAsyncStatus.status === ResourceTableStatusesEnum.LOADING;
  const isApplicationsLoading = hasNewApplicationsEntryPoints
    ? applicationsAsyncStatus.status === ResourceTableStatusesEnum.INITIAL ||
      applicationsAsyncStatus.status === ResourceTableStatusesEnum.LOADING
    : false;

  // If we are loading.
  if (isIntegrationTypesLoading || isApplicationsLoading) {
    return (
      <>
        <SidePanelApplicationsManagerInfoBox
          isInfoBoxExpanded={isInfoBoxExpanded}
          onToggleInfoBox={onToggleInfoBox}
        />
        <SidePanelApplicationsManagerFindMoreBanner />
        {renderHeader()}
        <ApplicationCardSkeleton />
        <ApplicationCardSkeleton />
        <ApplicationCardSkeleton />
        <ApplicationCardSkeleton />
      </>
    );
  }

  return (
    <>
      {!isSearching && (
        <>
          <SidePanelApplicationsManagerInfoBox
            isInfoBoxExpanded={isInfoBoxExpanded}
            onToggleInfoBox={onToggleInfoBox}
          />
          <SidePanelApplicationsManagerFindMoreBanner />
          {renderHeader()}
        </>
      )}
      {pinnedFrames.map((frame) => (
        <SidePanelApplicationFrameCard
          key={frame.frameId}
          frame={frame}
          isPinned
          onPinnedChange={() => {
            trackInterfaceEvent(
              InterfaceEventsEnum.APPS_P4_UNPIN,
              computeSidePanelTrackingTrackingFramePayload(frame) || {
                custom_id: computeSidePanelTrackingFrameName(frame),
              },
            );
            onTogglePinned(frame);
          }}
          integrationType={findIntegrationType(frame, integrationTypes)}
        />
      ))}
      {unpinnedFrames.map((frame) => (
        <SidePanelApplicationFrameCard
          key={frame.frameId}
          frame={frame}
          onPinnedChange={() => {
            trackInterfaceEvent(
              InterfaceEventsEnum.APPS_P4_PIN,
              computeSidePanelTrackingTrackingFramePayload(frame) || {
                custom_id: computeSidePanelTrackingFrameName(frame),
              },
            );
            onTogglePinned(frame);
          }}
          integrationType={findIntegrationType(frame, integrationTypes)}
        />
      ))}
    </>
  );
};

/*
 * Helpers.
 */

function renderHeader() {
  return (
    <StyledHeaderWrapperDiv>
      <StyledHeaderTitleDiv>
        <Trans id="sidepanel-applications-manager-header-title">Manage sidebar</Trans>
      </StyledHeaderTitleDiv>
      <StyledHeaderDescriptionDiv>
        <Trans id="sidepanel-applications-manager-header-description">
          Select which enabled plugins you want to see in your sidebar by pinning them.
        </Trans>
      </StyledHeaderDescriptionDiv>
    </StyledHeaderWrapperDiv>
  );
}

function findIntegrationType(frame: SidePanelFrame, integrationTypes: ResourceMap<IntegrationTypeModel>) {
  if (frame.frameType === FrameTypesEnum.INTEGRATION) {
    return integrationTypes[frame.type];
  }
  if (frame.frameType === FrameTypesEnum.ENTRY_POINT) {
    return integrationTypes[frame.applicationTypeId];
  }
  return undefined;
}

function renderDropdownLoader(fetchAsync: AsyncHandler) {
  // If the request failed, allow the user to retry fetching.
  return (
    <DropdownLoader
      size={VisualSizesEnum.EXTRA_LARGE}
      status={AsyncStatusesEnum.FAILED}
      onRetryClick={fetchAsync}
    />
  );
}
